import React, { useRef, useState } from 'react';
import { Button } from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import BootstrapTable from '../../../../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { DataTableCellUploadPopup } from '../../../../../DataRequest/DataTableCellUploadPopup/DataTableCellUploadPopup';
import Helpers from '../../../../../../utils/helper';
import { parseCurrencyValue } from './PriceSummaryTableRow';
import { TermsFormSelect } from './TermsFormSelect';
import { PendingChangeControl } from './PendingChangeControl';
import { PendingFieldChange } from '../../../../../../slices/marketplace/loi-form/pending-field-change';

interface TableRow {
    tableRowId: number;
}

interface PayoffRowData {
    id: string;
    estimatedPayOffAmount: number | null;
    calculationNotes: string | null;
}

type PayoffField = 'estimatedPayOffAmount' | 'calculationNotes';

interface VendorContractsTableProps {
    columns: string[];
    tableRows: TableRow[];
    selectedRowIds: number[];
    isReadonly: boolean;
    onAssumptionToggle?: (tableRowId: number, isAssumed: boolean) => void;
    rowDisplayData?: Record<number, Record<string, string>>;
    projectId?: number | null;
    tableId?: number | null;
    fileColumnName?: string | null;
    fileColumnId?: number | null;
    isAdmin?: boolean;
    showAssumptionLabel?: boolean;
    showPayoffColumns?: boolean;
    payoffByRow?: Record<number, PayoffRowData>;
    isPayoffEditable?: boolean;
    onPayoffCommit?: (tableRowId: number, field: PayoffField, value: number | string | null) => void;
    payoffTotal?: number;
    // Rows the buyer explicitly answered "Payoff at Close" for; rows in neither list are unanswered.
    payoffAtCloseRowIds?: number[];
    // Review views: an unanswered row predates the mandatory choice and effectively means "Payoff at Close".
    treatMissingAsPayoff?: boolean;
    hasError?: boolean;
    pendingChangesByRow?: Record<string, PendingFieldChange>;
    canApprovePendingChanges?: boolean;
    onApprovePendingChange?: (change: PendingFieldChange) => Promise<void>;
    onRejectPendingChange?: (change: PendingFieldChange) => Promise<void>;
    canDiscardPendingChanges?: boolean;
    onDiscardPendingChange?: (change: PendingFieldChange) => Promise<void>;
}

const PAYOFF_COLUMN_FIELD = 'estimatedPayOffAmount';
const TOTAL_ROW_LABEL = 'Total';
const EMPTY_FOOTER_VALUE = '';

const ASSUMPTION_OPTION_ASSUME = 'Assume';
const ASSUMPTION_OPTION_PAYOFF = 'Payoff at Close';
const ASSUMPTION_OPTIONS = [
    { value: ASSUMPTION_OPTION_ASSUME },
    { value: ASSUMPTION_OPTION_PAYOFF },
] as const;

const EMPTY_PAYOFF_CELL = '—';

const PayoffAmountCell = ({
    tableRowId,
    initialValue,
    isEditable,
    onCommit,
}: {
    tableRowId: number;
    initialValue: number | null;
    isEditable: boolean;
    onCommit?: (tableRowId: number, field: PayoffField, value: number | string | null) => void;
}) => {
    const [value, setValue] = useState<string>(initialValue != null ? String(initialValue) : '');

    if (!isEditable) {
        return <span>{initialValue != null ? Helpers.currencyFormat(initialValue, false) : EMPTY_PAYOFF_CELL}</span>;
    }

    return (
        <CurrencyInput
            className="form-control loi-form-input loi-vendor-contracts-table__payoff-input"
            value={value}
            allowDecimals={false}
            decimalsLimit={0}
            allowNegativeValue={false}
            prefix="$"
            onValueChange={(v) => setValue(v ?? '')}
            onBlur={() => onCommit?.(tableRowId, 'estimatedPayOffAmount', parseCurrencyValue(value))}
        />
    );
};

const PayoffNotesCell = ({
    tableRowId,
    initialValue,
    isEditable,
    onCommit,
}: {
    tableRowId: number;
    initialValue: string | null;
    isEditable: boolean;
    onCommit?: (tableRowId: number, field: PayoffField, value: number | string | null) => void;
}) => {
    const [value, setValue] = useState<string>(initialValue ?? '');

    if (!isEditable) {
        return <span>{initialValue ?? EMPTY_PAYOFF_CELL}</span>;
    }

    return (
        <input
            type="text"
            className="loi-vendor-contracts-table__notes-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={() => onCommit?.(tableRowId, 'calculationNotes', value.trim() === '' ? null : value)}
        />
    );
};

export const VendorContractsTable = ({
    columns,
    tableRows,
    selectedRowIds,
    isReadonly,
    onAssumptionToggle,
    rowDisplayData,
    projectId,
    tableId,
    fileColumnName,
    fileColumnId,
    isAdmin,
    showAssumptionLabel = false,
    showPayoffColumns = false,
    payoffByRow,
    isPayoffEditable = false,
    onPayoffCommit,
    payoffTotal,
    payoffAtCloseRowIds = [],
    treatMissingAsPayoff = false,
    hasError = false,
    pendingChangesByRow = {},
    canApprovePendingChanges = false,
    onApprovePendingChange,
    onRejectPendingChange,
    canDiscardPendingChanges = false,
    onDiscardPendingChange,
}: VendorContractsTableProps) => {
    const handleAssumptionChange = (tableRowId: number, value: string | null) => {
        if (value === null) {
            return;
        }
        onAssumptionToggle?.(tableRowId, value === ASSUMPTION_OPTION_ASSUME);
    };
    const handleAssumptionChangeRef = useRef(handleAssumptionChange);
    handleAssumptionChangeRef.current = handleAssumptionChange;

    const [selectedRowTableRowId, setSelectedRowTableRowId] = useState<number | null>(null);
    const [isFilePopupVisible, setIsFilePopupVisible] = useState(false);

    const tableColumns = [
        ...columns.map((col) => {
            if (col === fileColumnName) {
                return {
                    dataField: col,
                    text: col,
                    isDummyField: true,
                    formatter: (_: unknown, row: TableRow) => (
                        <Button
                            color="primary"
                            size="sm"
                            onClick={() => {
                                setSelectedRowTableRowId(row.tableRowId);
                                setIsFilePopupVisible(true);
                            }}>
                            Files
                        </Button>
                    ),
                };
            }
            return {
                dataField: col,
                text: col,
                isDummyField: true,
                formatter: (_: unknown, row: TableRow) =>
                    rowDisplayData?.[row.tableRowId]?.[col] ?? '',
            };
        }),
        {
            dataField: 'tableRowId',
            text: showAssumptionLabel ? 'Buyer Assumption' : 'Assume Obligation',
            headerAlign: 'center' as const,
            align: 'center' as const,
            formatExtraData: {
                isReadonly,
                selectedRowIds,
                payoffAtCloseRowIds,
                treatMissingAsPayoff,
                hasError,
                pendingChangesByRow,
                canApprovePendingChanges,
                onApprovePendingChange,
                onRejectPendingChange,
                canDiscardPendingChanges,
                onDiscardPendingChange,
            },
            formatter: (
                _: number,
                row: TableRow,
                _rowIndex: number,
                extra: {
                    isReadonly: boolean;
                    selectedRowIds: number[];
                    payoffAtCloseRowIds: number[];
                    treatMissingAsPayoff: boolean;
                    hasError: boolean;
                    pendingChangesByRow: Record<string, PendingFieldChange>;
                    canApprovePendingChanges: boolean;
                    onApprovePendingChange?: (change: PendingFieldChange) => Promise<void>;
                    onRejectPendingChange?: (change: PendingFieldChange) => Promise<void>;
                    canDiscardPendingChanges: boolean;
                    onDiscardPendingChange?: (change: PendingFieldChange) => Promise<void>;
                }
            ) => {
                const answer: boolean | null = extra.selectedRowIds.includes(row.tableRowId)
                    ? true
                    : extra.payoffAtCloseRowIds.includes(row.tableRowId) || extra.treatMissingAsPayoff
                        ? false
                        : null;
                const pendingChange = extra.pendingChangesByRow[String(row.tableRowId)];
                const displayedAnswer = pendingChange ? pendingChange.proposedValue === 'true' : answer;
                if (showAssumptionLabel) {
                    return <span>{answer === true ? 'Assumed' : 'Not Assumed'}</span>;
                }
                const assumptionSelect = (
                    <TermsFormSelect
                        value={displayedAnswer === null ? '' : displayedAnswer ? ASSUMPTION_OPTION_ASSUME : ASSUMPTION_OPTION_PAYOFF}
                        options={ASSUMPTION_OPTIONS}
                        className="vendor-contract-assumption-select"
                        disabled={extra.isReadonly || (!!pendingChange && !isAdmin)}
                        invalid={extra.hasError && answer === null}
                        onChange={(value) => handleAssumptionChangeRef.current(row.tableRowId, value)}
                        aria-label="Assume Obligation"
                    />
                );
                return (
                    <div className="loi-vendor-contracts-table__assumption-cell">
                        {pendingChange ? (
                            <PendingChangeControl
                                canApprove={extra.canApprovePendingChanges}
                                onApprove={() => extra.onApprovePendingChange?.(pendingChange) ?? Promise.resolve()}
                                onReject={() => extra.onRejectPendingChange?.(pendingChange) ?? Promise.resolve()}
                                onDiscard={extra.canDiscardPendingChanges ? () => extra.onDiscardPendingChange?.(pendingChange) ?? Promise.resolve() : undefined}>
                                {assumptionSelect}
                            </PendingChangeControl>
                        ) : assumptionSelect}
                    </div>
                );
            },
        },
        ...(showPayoffColumns
            ? [
                  {
                      dataField: PAYOFF_COLUMN_FIELD,
                      text: 'Estimated Pay-Off Amount',
                      isDummyField: true,
                      formatter: (_: unknown, row: TableRow) => {
                          if (selectedRowIds.includes(row.tableRowId)) {
                              return null;
                          }
                          const payoff = payoffByRow?.[row.tableRowId];
                          return (
                              <PayoffAmountCell
                                  tableRowId={row.tableRowId}
                                  initialValue={payoff?.estimatedPayOffAmount ?? null}
                                  isEditable={isPayoffEditable}
                                  onCommit={onPayoffCommit}
                              />
                          );
                      },
                  },
                  {
                      dataField: 'calculationNotes',
                      text: 'Calculation Notes',
                      isDummyField: true,
                      formatter: (_: unknown, row: TableRow) => {
                          if (selectedRowIds.includes(row.tableRowId)) {
                              return null;
                          }
                          const payoff = payoffByRow?.[row.tableRowId];
                          return (
                              <PayoffNotesCell
                                  tableRowId={row.tableRowId}
                                  initialValue={payoff?.calculationNotes ?? null}
                                  isEditable={isPayoffEditable}
                                  onCommit={onPayoffCommit}
                              />
                          );
                      },
                  },
              ]
            : []),
    ];

    const showTotalFooter = showPayoffColumns && payoffTotal !== undefined;
    const tableColumnsWithFooter = showTotalFooter
        ? tableColumns.map((column, index) => {
              if (index === 0) {
                  return { ...column, footer: TOTAL_ROW_LABEL };
              }
              if (column.dataField === PAYOFF_COLUMN_FIELD) {
                  return { ...column, footer: Helpers.currencyFormat(payoffTotal ?? 0, false) };
              }
              return { ...column, footer: EMPTY_FOOTER_VALUE };
          })
        : tableColumns;

    return (
        <div className="loi-vendor-contracts-table table-responsive">
            <BootstrapTable
                bootstrap4
                keyField="tableRowId"
                data={tableRows}
                columns={tableColumnsWithFooter}
                noDataIndication="No vendors available"
            />
            {isFilePopupVisible &&
                selectedRowTableRowId != null &&
                projectId &&
                tableId &&
                fileColumnId != null && (
                    <DataTableCellUploadPopup
                        projectId={projectId}
                        tableId={tableId}
                        columnId={fileColumnId}
                        rowId={selectedRowTableRowId}
                        isReadonlyMode={true}
                        isAdminView={isAdmin ?? false}
                        close={() => setIsFilePopupVisible(false)}
                        onSuccessfulUpload={() => {}}
                        title={fileColumnName || undefined}
                    />
                )}
        </div>
    );
};
